import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AuthContext, type AuthStatus, type ResultadoLogin } from './AuthContext'
import { authApi, type LoginInput } from './authApi'
import { isMfaChallenge, mfaApi } from './mfaApi'
import { tokenStore } from './tokenStore'
import { useHubLembrado } from '../hubs/hubs'
import { setUnauthorizedHandler } from '../api/client'
import type { LoginResponse, Usuario } from '../api/types'

function finishLoader() {
  window.AppLoader?.finish()
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<Usuario | null>(null)
  const [status, setStatus] = useState<AuthStatus>('loading')
  // Cada hub tem a própria sessão (03/11/2026). Trocar o hub da aba troca a sessão
  // em uso: a do hub novo, se houver, ou o login dele. A do outro fica guardada.
  const hub = useHubLembrado()
  const hubAnterior = useRef(hub)

  const logout = useCallback(() => {
    tokenStore.clear()
    setUser(null)
    setStatus('unauthenticated')
    queryClient.clear()
  }, [queryClient])

  /** Aplica a sessão recebida. Ponto único onde o token entra no armazenamento.
   *
   *  `senha_temporaria` acompanha o usuário porque o gate de navegação depende
   *  de COMO a sessão foi aberta, e não do estado da conta no banco. */
  const aplicarSessao = useCallback((res: LoginResponse): Usuario => {
    const usuario: Usuario = { ...res.usuario, senha_temporaria: Boolean(res.senha_temporaria) }
    tokenStore.set(res.access_token, res.refresh_token)
    setUser(usuario)
    setStatus('authenticated')
    return usuario
  }, [])

  const login = useCallback(async (input: LoginInput): Promise<ResultadoLogin> => {
    const res = await authApi.login(input)
    // Com segundo fator ativo não há token nenhum aqui: só o desafio. Nada é
    // gravado no tokenStore antes do código ser verificado.
    if (isMfaChallenge(res)) {
      return { tipo: 'mfa', desafio: res.challenge, expiraEm: res.expires_in }
    }
    const usuario = aplicarSessao(res)
    return {
      tipo: 'sessao',
      usuario,
      configurarMfa: Boolean(res.mfa_setup_required),
      senhaTemporaria: Boolean(res.senha_temporaria),
    }
  }, [aplicarSessao])

  const completarMfa = useCallback(
    async (desafio: string, codigo: string): Promise<Usuario> => {
      return aplicarSessao(await mfaApi.verify(desafio, codigo))
    },
    [aplicarSessao],
  )

  // Sessão perdida (refresh falhou / token revogado) → derruba a UI.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null)
      setStatus('unauthenticated')
      queryClient.clear()
    })
    return () => setUnauthorizedHandler(null)
  }, [queryClient])

  // Bootstrap: se há refresh salvo, recupera o usuário; sempre encerra o loader.
  // Roda de novo a cada troca de hub, na sessão do hub novo.
  useEffect(() => {
    let active = true
    if (hubAnterior.current !== hub) {
      hubAnterior.current = hub
      setUser(null)
      setStatus('loading')
      queryClient.clear()
    }
    async function boot() {
      if (!tokenStore.hasSession()) {
        if (active) setStatus('unauthenticated')
        finishLoader()
        return
      }
      try {
        const u = await authApi.me()
        if (active) {
          setUser(u)
          setStatus('authenticated')
        }
      } catch {
        tokenStore.clear()
        if (active) {
          setUser(null)
          setStatus('unauthenticated')
        }
      } finally {
        finishLoader()
      }
    }
    void boot()
    return () => {
      active = false
    }
  }, [hub, queryClient])

  /** Recarrega o usuário logado, com as permissões do momento.
   *
   *  A sessão era buscada UMA vez, na montagem, e nunca mais: revogar uma
   *  permissão de quem está logado não tinha efeito nenhum na interface até a
   *  pessoa recarregar a página. O servidor já negava as chamadas, então a tela
   *  ficava mostrando itens de menu que respondiam 403.
   *
   *  `senha_temporaria` é preservada de propósito: ela descreve COMO a sessão foi
   *  aberta, não o estado da conta no banco, e /auth/me não a devolve. Perdê-la
   *  aqui liberaria a navegação de quem entrou com senha temporária. */
  const revalidar = useCallback(async () => {
    if (!tokenStore.hasSession()) return
    try {
      const u = await authApi.me()
      setUser((anterior) =>
        anterior ? { ...u, senha_temporaria: anterior.senha_temporaria } : u,
      )
    } catch {
      /* Falha aqui não derruba a sessão: o interceptor de 401 já cuida disso, e
         uma queda de rede não pode deslogar quem estava trabalhando. */
    }
  }, [])

  // Revalida ao voltar o foco para a aba. É o gatilho mais próximo do momento em
  // que a mudança acontece: o administrador salva numa aba, a pessoa volta para a
  // dela e o menu já reflete o acesso novo.
  useEffect(() => {
    function aoFocar() {
      if (document.visibilityState === 'visible') void revalidar()
    }
    window.addEventListener('focus', aoFocar)
    document.addEventListener('visibilitychange', aoFocar)
    return () => {
      window.removeEventListener('focus', aoFocar)
      document.removeEventListener('visibilitychange', aoFocar)
    }
  }, [revalidar])

  const value = useMemo(
    () => ({
      user,
      status,
      precisaCriarSenha: Boolean(user?.senha_temporaria),
      login,
      completarMfa,
      aplicarSessao,
      revalidar,
      logout,
    }),
    [user, status, login, completarMfa, aplicarSessao, revalidar, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
